/**
 * Key capture. Binds by `event.code` so the physical key positions are the same on every
 * layout, ignores auto-repeat, and reports run presses, the action key and shortcuts.
 */
import type { RunKey } from '@decathlon/shared';

export interface KeyBinding {
  id: string;
  name: string;
  left: string;
  right: string;
  blurb: string;
}

export const BINDINGS: KeyBinding[] = [
  { id: 'zx', name: 'Z / X', left: 'KeyZ', right: 'KeyX', blurb: 'Classic left-hand hammer' },
  {
    id: 'arrows',
    name: '← / →',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    blurb: 'Two fingers, right hand',
  },
  { id: 'al', name: 'A / L', left: 'KeyA', right: 'KeyL', blurb: 'One finger per hand' },
  { id: 'comma', name: ', / .', left: 'Comma', right: 'Period', blurb: 'Right-hand hammer' },
];
export const DEFAULT_BINDING = 'zx';
const KEY = 'decathlon.binding';

export function bindingById(id: string): KeyBinding {
  if (id === 'custom') {
    try {
      const b = JSON.parse(localStorage.getItem('decathlon.customBinding') ?? 'null') as KeyBinding;
      if (b && validRunCode(b.left) && validRunCode(b.right) && b.left !== b.right)
        return { ...b, id: 'custom', name: 'Custom', blurb: 'Your run keys' };
    } catch {
      /* unavailable */
    }
  }
  return BINDINGS.find((b) => b.id === id) ?? BINDINGS[0]!;
}

export function loadBinding(): KeyBinding {
  try {
    return bindingById(localStorage.getItem(KEY) ?? DEFAULT_BINDING);
  } catch {
    return BINDINGS[0]!;
  }
}

export function saveBinding(id: string): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* ignore */
  }
}

export type ControlEvent =
  | { type: 'run'; key: RunKey }
  | { type: 'action'; down: boolean }
  | { type: 'camera' }
  | { type: 'quit' }
  | { type: 'confirm' };

const editable = (e: KeyboardEvent) =>
  e.target instanceof Element && !!e.target.closest('input,textarea,select,[contenteditable=true]');

/** Listen for game keys until the returned function is called. */
export function attachControls(
  binding: KeyBinding,
  cb: (e: ControlEvent) => void,
  active: () => boolean = () => true,
): () => void {
  let actionHeld = false;
  const down = (e: KeyboardEvent) => {
    if (editable(e)) return;
    if (e.repeat) return; // holding a key never counts as alternating
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.code === 'Escape') return cb({ type: 'quit' });
    if (!active()) return;
    // Native keyboard activation must still work for focused HUD controls.
    if (
      (e.code === 'Space' || e.code === 'Enter' || e.code === 'NumpadEnter') &&
      e.target instanceof Element &&
      e.target.closest('button,a')
    )
      return;
    switch (e.code) {
      case binding.left:
        e.preventDefault();
        (e.target as HTMLElement | null)?.blur?.();
        return cb({ type: 'run', key: 0 });
      case binding.right:
        e.preventDefault();
        (e.target as HTMLElement | null)?.blur?.();
        return cb({ type: 'run', key: 1 });
      case 'Space':
        actionHeld = true;
        e.preventDefault();
        return cb({ type: 'action', down: true });
      case 'Enter':
      case 'NumpadEnter':
        return cb({ type: 'confirm' });
    }
    if (e.key === 'Enter') cb({ type: 'confirm' });
  };
  const up = (e: KeyboardEvent) => {
    if (e.code === 'Space' && actionHeld) {
      actionHeld = false;
      e.preventDefault();
      cb({ type: 'action', down: false });
    }
  };
  const release = () => {
    if (actionHeld) {
      actionHeld = false;
      cb({ type: 'action', down: false });
    }
  };
  const visibility = () => {
    if (document.hidden) release();
  };
  addEventListener('blur', release);
  document.addEventListener('visibilitychange', visibility);
  addEventListener('keydown', down);
  addEventListener('keyup', up);
  return () => {
    release();
    removeEventListener('blur', release);
    document.removeEventListener('visibilitychange', visibility);
    removeEventListener('keydown', down);
    removeEventListener('keyup', up);
  };
}

export function validRunCode(code: string): boolean {
  return /^(Key[A-Z]|Digit[0-9]|Arrow(Left|Right|Up|Down)|Comma|Period|Semicolon|Slash|BracketLeft|BracketRight)$/.test(
    code,
  );
}
export function saveCustomBinding(left: string, right: string): boolean {
  if (!validRunCode(left) || !validRunCode(right) || left === right) return false;
  try {
    localStorage.setItem('decathlon.customBinding', JSON.stringify({ left, right }));
    saveBinding('custom');
    return true;
  } catch {
    return false;
  }
}
